// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_280,
  PERFECT_SCROLLBAR_MAX_320
} from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardIndustrial.json";

import "react-table/react-table.css";

function IndustrialDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const maintenanceColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "industrial.col.wo" }),
        accessor: "id",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "industrial.col.asset" }), accessor: "asset" },
      { Header: formatMessage({ id: "industrial.col.type" }), accessor: "type" },
      { Header: formatMessage({ id: "industrial.col.due" }), accessor: "due" },
      {
        Header: formatMessage({ id: "industrial.col.priority" }),
        accessor: "priority",
        Cell: p => (
          <Badge
            color={
              p.value === "high"
                ? "danger"
                : p.value === "medium"
                  ? "warning"
                  : "secondary"
            }
          >
            {p.value}
          </Badge>
        )
      }
    ];
    }, [intl]);

  const outputOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 2], curve: "smooth", dashArray: [0, 6] },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor4],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.outputTrend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const energyOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      colors: [c.ReactifyChartColor2],
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.energy.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${addCommas(v)} kWh`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const shiftOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 6, columnWidth: "40%" } },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3],
      dataLabels: { enabled: true },
      xaxis: {
        categories: data.shifts.map(s => s.name),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: { style: { colors: c.primaryColor } },
        title: { text: "Units" }
      },
      legend: { position: "top" },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const downtimeOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.downtime.map(d => `${d.reason} (${d.line})`),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "60%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const { formatMessage } = intl;
  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--industrial">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.industrial" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-3">
            <Colxx xxs="12" className="d-flex flex-wrap gap-2 align-items-center">
              <Button color="primary" size="sm">
                <IntlMessages id="industrial.toolbar.ack-alarms" />
              </Button>
              <Button color="outline-secondary" size="sm">
                <IntlMessages id="industrial.toolbar.shift-report" />
              </Button>
              <Badge color="danger" pill className="ms-2">
                {k.activeAlarms}{" "}
                <IntlMessages id="industrial.badge.active-alarms" />
              </Badge>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.oeeSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.oee" />
                  </p>
                  <h4 className="mb-0">{k.oee}%</h4>
                  <span className="text-success small">+{k.oeeDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-industry text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.throughput" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.throughput)}</h4>
                  <span className="text-success small">+{k.throughputDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-pause-circle text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.downtime" />
                  </p>
                  <h4 className="mb-0">{k.downtimePct}%</h4>
                  <span className="text-success small">{k.downtimeDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-check-square-o text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.quality" />
                  </p>
                  <h4 className="mb-0">{k.qualityRate}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-bolt text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.energy" />
                  </p>
                  <h4 className="mb-0">
                    {k.energyMwh}{" "}
                    <span className="h6 text-muted">MWh</span>
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-wrench text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.work-orders" />
                  </p>
                  <h4 className="mb-0">{k.openWorkOrders}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-cogs text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.lines" />
                  </p>
                  <h4 className="mb-0">{data.lines.length}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-shield text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="industrial.kpi.safety-days" />
                  </p>
                  <h4 className="mb-0">412</h4>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.chart.output" />
                  </CardTitle>
                  <Chart
                    options={outputOptions}
                    series={[
                      { name: "Actual output", data: data.outputTrend.actual },
                      { name: "Target", data: data.outputTrend.target }
                    ]}
                    type="line"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.chart.downtime" />
                  </CardTitle>
                  <Chart
                    options={downtimeOptions}
                    series={data.downtime.map(d => d.minutes)}
                    type="donut"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.section.lines" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_280}>
                    <ul className="list-unstyled mb-0">
                      {data.lines.map((line, i) => (
                        <li
                          key={i}
                          className="border-bottom pb-3 mb-3"
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="font-weight-medium">{line.name}</span>
                            <Badge
                              color={
                                line.status === "running"
                                  ? "success"
                                  : line.status === "changeover"
                                    ? "warning"
                                    : "secondary"
                              }
                            >
                              {line.status}
                            </Badge>
                          </div>
                          <div className="small text-muted mb-1">
                            OEE {line.oee}% ·{" "}
                            <IntlMessages id="industrial.label.output" />{" "}
                            {addCommas(line.output)}
                          </div>
                          <Progress value={line.oee} />
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.chart.energy" />
                  </CardTitle>
                  <Chart
                    options={energyOptions}
                    series={[
                      { name: "kWh", data: data.energy.kwh }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.chart.shifts" />
                  </CardTitle>
                  <Chart
                    options={shiftOptions}
                    series={[
                      {
                        name: formatMessage({ id: "industrial.legend.planned" }),
                        data: data.shifts.map(s => s.planned)
                      },
                      {
                        name: formatMessage({ id: "industrial.legend.actual" }),
                        data: data.shifts.map(s => s.actual)
                      }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.section.alarms" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_320}>
                    <ul className="list-unstyled mb-0">
                      {data.alarms.map((a, i) => (
                        <li
                          key={i}
                          className="border-bottom pb-3 mb-3"
                        >
                          <div className="d-flex justify-content-between">
                            <Badge
                              color={
                                a.severity === "critical"
                                  ? "danger"
                                  : a.severity === "warning"
                                    ? "warning"
                                    : "info"
                              }
                              className="me-2"
                            >
                              {a.code}
                            </Badge>
                            <span className="text-muted small">{a.time}</span>
                          </div>
                          <p className="mb-0 mt-2 small">{a.text}</p>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.table.maintenance" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.maintenance}
                    columns={maintenanceColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="industrial.section.safety" />
                  </CardTitle>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>{formatMessage({ id: "industrial.col.date" })}</th>
                          <th>{formatMessage({ id: "industrial.col.type" })}</th>
                          <th>{formatMessage({ id: "industrial.col.area" })}</th>
                          <th>{formatMessage({ id: "industrial.col.status" })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.safety.map((s, i) => (
                          <tr key={i}>
                            <td>{s.date}</td>
                            <td>{s.type}</td>
                            <td>{s.area}</td>
                            <td>
                              <Badge
                                color={s.status === "Open" ? "warning" : "success"}
                              >
                                {s.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default IndustrialDashboard;
